package org.example.project.enginepersonal

import org.example.project.settings.UISettings

import androidx.compose.runtime.Composable
import org.example.project.dashboard.DashboardTheme
import org.example.project.dashboard.PopupEngineCard
import org.example.project.enginetabular.TableStyles
import org.example.project.screens_cus.Urlaubsanspruch
import org.example.project.service.ZustandManager
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Style
import org.jetbrains.compose.web.dom.Text

@Composable
internal fun PersonalKontingentLegend(
    includeTimeStates: Boolean,
    singleLine: Boolean = false
) {
    Style(PersonalKennzahlenStyles)
    val items = buildList {
        add("Beantragt/Bearbeitung" to ZustandManager.Urlaub.calendarColorHexFor(ZustandManager.Urlaub.BEANTRAGT))
        add("Genehmigt" to ZustandManager.Urlaub.calendarColorHexFor(ZustandManager.Urlaub.GENEHMIGT))
        add("Genommen" to ZustandManager.Urlaub.calendarColorHexFor(ZustandManager.Urlaub.GENEHMIGT, genommen = true))
        add("Abgelehnt/Storniert" to ZustandManager.Urlaub.calendarColorHexFor(ZustandManager.Urlaub.STORNIERT))
        add("Zeitausgleich" to ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.FREIZEITAUSGLEICH))
        add("Sonderurlaub" to ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.URLAUB))
        add("Unbezahlt" to ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.FEHLTAG))
        if (includeTimeStates) {
            add("Krank" to ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.KRANK))
            add("Zeitbuchung" to ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.SOLL_ERFUELLT))
            add("Fehlbuchung" to ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.FEHLBUCHUNG))
            add("Feiertag" to ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.FEIERTAG))
        }
    }
    Div({
        classes(PersonalKennzahlenStyles.legend)
        if (singleLine) classes(PersonalKennzahlenStyles.legendSingleLine)
    }) {
        items.forEach { (label, color) ->
            Div({
                classes(PersonalKennzahlenStyles.legendItem)
                attr("title", label)
            }) {
                Span({
                    classes(PersonalKennzahlenStyles.legendDot)
                    style { backgroundColor(Color(color)) }
                })
                Span({ classes(PersonalKennzahlenStyles.legendLabel) }) { Text(label) }
            }
        }
    }
}

@Composable
internal fun PersonalIstLegend(singleLine: Boolean = false) {
    Style(PersonalKennzahlenStyles)
    val items = listOf(
        "Urlaub genommen" to ZustandManager.Urlaub.calendarColorHexFor(ZustandManager.Urlaub.GENEHMIGT, genommen = true),
        "Zeitausgleich gebucht" to ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.FREIZEITAUSGLEICH),
        "Sonderurlaub" to ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.URLAUB),
        "Unbezahlt frei" to ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.FEHLTAG),
        "Krank" to ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.KRANK),
        "Zeitbuchung" to ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.SOLL_ERFUELLT),
        "Feiertag" to ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.FEIERTAG)
    )
    Div({
        classes(PersonalKennzahlenStyles.legend)
        if (singleLine) classes(PersonalKennzahlenStyles.legendSingleLine)
    }) {
        items.forEach { (label, color) ->
            Div({ classes(PersonalKennzahlenStyles.legendItem); attr("title", label) }) {
                Span({ classes(PersonalKennzahlenStyles.legendDot); style { backgroundColor(Color(color)) } })
                Span({ classes(PersonalKennzahlenStyles.legendLabel) }) { Text(label) }
            }
        }
    }
}

@Composable
internal fun PersonalKontingentStrip(allowance: Urlaubsanspruch?) {
    Style(PersonalKennzahlenStyles)
    val metrics = listOf(
        Triple("Urlaubsanspruch", personalKennzahlDays(allowance?.gesamtanspruch ?: 0.0), ""),
        Triple("Urlaub reserviert", personalKennzahlDays(allowance?.urlaubReserviert ?: 0.0), if ((allowance?.urlaubReserviert ?: 0.0) > 0.0) "reserved" else ""),
        Triple("Urlaub genommen", personalKennzahlDays(allowance?.urlaubGenommen ?: 0.0), if ((allowance?.urlaubGenommen ?: 0.0) > 0.0) "negative" else ""),
        Triple("Urlaub verf\u00fcgbar", personalKennzahlDays(allowance?.verfuegbar ?: 0.0), if ((allowance?.verfuegbar ?: 0.0) > 0.0) "available" else "negative"),
        Triple("Zeitkonto", personalKennzahlHours(allowance?.stundenkonto ?: 0.0), ""),
        Triple("Zeitreserve Soll", personalKennzahlHours(allowance?.zeitreserve ?: 0.0), ""),
        Triple("Zeitreserve Ist", personalKennzahlHours(allowance?.zeitreserveIst ?: 0.0), ""),
        Triple("\u00dcberstunden reserviert", personalKennzahlHours(allowance?.reservierteUeberstunden ?: 0.0), if ((allowance?.reservierteUeberstunden ?: 0.0) > 0.0) "reserved" else ""),
        Triple("\u00dcberstunden genommen", personalKennzahlHours(allowance?.ueberstundenGenommen ?: 0.0), if ((allowance?.ueberstundenGenommen ?: 0.0) > 0.0) "negative" else ""),
        Triple("\u00dcberstunden verf\u00fcgbar", personalKennzahlHours(allowance?.zeitkontoVerfuegbar ?: 0.0), if ((allowance?.zeitkontoVerfuegbar ?: 0.0) > 0.0) "available" else "negative"),
        Triple("Tagesarbeitszeit", personalKennzahlHours(allowance?.arbeitszeitTaeglich ?: 0.0), ""),
        Triple("Arbeitszeit monatlich", personalKennzahlHours(allowance?.arbeitszeitMonatlich ?: 0.0), "")
    )
    Div({ classes(PersonalKennzahlenStyles.strip) }) {
        metrics.forEach { (label, value, tone) ->
            Div({ classes(PersonalKennzahlenStyles.stripMetric) }) {
                Span({ classes(PersonalKennzahlenStyles.stripLabel) }) { Text(label) }
                Span({
                    classes(PersonalKennzahlenStyles.stripValue)
                    when (tone) {
                        "available" -> classes(PersonalKennzahlenStyles.stripValueAvailable)
                        "reserved" -> classes(PersonalKennzahlenStyles.stripValueReserved)
                        "negative" -> classes(PersonalKennzahlenStyles.stripValueNegative)
                    }
                }) { Text(value) }
            }
        }
    }
}

@Composable
internal fun PersonalKennzahlenInfoButton(onClick: () -> Unit) {
    Style(PersonalKennzahlenStyles)
    Button(attrs = {
        classes(TableStyles.topBarButton, PersonalKennzahlenStyles.infoButton)
        attr("type", "button")
        attr("title", "Kennzahlen")
        attr("aria-label", "Kennzahlen")
        onClick { onClick() }
    }) {
        Img(src = "images/info.svg", attrs = {
            classes(TableStyles.topBarIcon, PersonalKennzahlenStyles.infoIcon)
            attr("alt", "")
        })
    }
}

@Composable
internal fun PersonalKennzahlenPopup(
    userName: String,
    allowance: Urlaubsanspruch?,
    onClose: () -> Unit
) {
    Style(PersonalKennzahlenStyles)
    PopupEngineCard(
        windowKey = "personal-kennzahlen-popup",
        widthPx = 460,
        heightPx = 520,
        minWidthPx = 360,
        minHeightPx = 360,
        initialX = 260.0,
        initialY = 120.0,
        resizable = true,
        viewportAnchored = true,
        contentBackground = DashboardTheme.POPUP_BG,
        frameBackground = DashboardTheme.POPUP_BG,
        onClose = onClose
    ) {
        Div({ classes(PersonalKennzahlenStyles.box) }) {
            H3({ classes(PersonalKennzahlenStyles.title) }) { Text(userName.ifBlank { "Kennzahlen" }) }
            Div({ classes(PersonalKennzahlenStyles.table) }) {
                PersonalKennzahlSection("Urlaub")
                PersonalKennzahl("Urlaubsanspruch", personalKennzahlDays(allowance?.gesamtanspruch ?: 0.0))
                PersonalKennzahl("Urlaub reserviert", personalKennzahlDays(allowance?.urlaubReserviert ?: 0.0))
                PersonalKennzahl("Urlaub genommen", personalKennzahlDays(allowance?.urlaubGenommen ?: 0.0))
                PersonalKennzahl("Urlaub verf\u00fcgbar", personalKennzahlDays(allowance?.verfuegbar ?: 0.0))
                PersonalKennzahlSection("Zeitkonto")
                PersonalKennzahl("Zeitkonto", personalKennzahlHours(allowance?.stundenkonto ?: 0.0))
                PersonalKennzahl("Zeitreserve Soll", personalKennzahlHours(allowance?.zeitreserve ?: 0.0))
                PersonalKennzahl("Zeitreserve Ist", personalKennzahlHours(allowance?.zeitreserveIst ?: 0.0))
                PersonalKennzahl("\u00dcberstunden reserviert", personalKennzahlHours(allowance?.reservierteUeberstunden ?: 0.0))
                PersonalKennzahl("\u00dcberstunden genommen", personalKennzahlHours(allowance?.ueberstundenGenommen ?: 0.0))
                PersonalKennzahl("\u00dcberstunden verf\u00fcgbar", personalKennzahlHours(allowance?.zeitkontoVerfuegbar ?: 0.0))
                PersonalKennzahlSection("Arbeitszeit")
                PersonalKennzahl("Tagesarbeitszeit", personalKennzahlHours(allowance?.arbeitszeitTaeglich ?: 0.0))
                PersonalKennzahl("Arbeitszeit monatlich", personalKennzahlHours(allowance?.arbeitszeitMonatlich ?: 0.0))
            }
        }
    }
}

@Composable
private fun PersonalKennzahlSection(label: String) {
    Div({ classes(PersonalKennzahlenStyles.sectionRow) }) {
        Span({ classes(PersonalKennzahlenStyles.sectionLabel) }) { Text(label) }
    }
}

@Composable
private fun PersonalKennzahl(label: String, value: String) {
    Div({ classes(PersonalKennzahlenStyles.tableRow) }) {
        Span({ classes(PersonalKennzahlenStyles.tableCell) }) { Text(label) }
        Span({ classes(PersonalKennzahlenStyles.tableValue) }) { Text(value) }
    }
}

private fun personalKennzahlDays(value: Double): String =
    "${personalKennzahlNumber(value)} Tage"

private fun personalKennzahlHours(value: Double): String =
    "${personalKennzahlNumber(value)} Std."

private fun personalKennzahlNumber(value: Double): String =
    if (value % 1.0 == 0.0) value.toInt().toString() else value.toString().replace(".", ",")

private object PersonalKennzahlenStyles : StyleSheet() {
    val legend by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        flexWrap(FlexWrap.Wrap)
        gap(4.px, 9.px)
        maxWidth(620.px)
        property("width", "fit-content")
        property("margin", "8px auto 10px")
    }
    val legendItem by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(4.px)
        property("white-space", "nowrap")
    }
    val legendSingleLine by style {
        width(100.percent)
        maxWidth(100.percent)
        flexWrap(FlexWrap.Nowrap)
        property("margin", "0")
        overflowX("auto")
        overflowY("hidden")
    }
    val legendDot by style {
        width(8.px)
        height(8.px)
        flexShrink(0)
        borderRadius(50.percent)
        property("box-shadow", "0 0 0 1px rgba(255,255,255,0.25)")
    }
    val legendLabel by style {
        color(rgba(255, 255, 255, 0.68))
        fontSize(10.px)
        fontWeight("700")
    }
    val strip by style {
        flexShrink(0)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Stretch)
        flexWrap(FlexWrap.Nowrap)
        gap(6.px)
        padding(6.px, 14.px, 8.px)
        overflowX("auto")
        overflowY("hidden")
        boxSizing("border-box")
        property("scrollbar-width", "thin")
    }
    val stripMetric by style {
        property("flex", "1 1 auto")
        property("min-width", "max-content")
        padding(7.px, 9.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(3.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.10))
        borderRadius(5.px)
        backgroundColor(rgba(255, 255, 255, 0.035))
        boxSizing("border-box")
    }
    val stripLabel by style {
        color(rgba(255, 255, 255, 0.58))
        fontSize(11.px)
        fontWeight("800")
        property("white-space", "nowrap")
    }
    val stripValue by style {
        color(Color.white)
        fontSize(14.px)
        fontWeight("900")
        property("white-space", "nowrap")
    }
    val stripValueAvailable by style {
        color(Color("#66D17A"))
    }
    val stripValueReserved by style {
        color(Color("#F2C94C"))
    }
    val stripValueNegative by style {
        color(UISettings.ACCENT)
    }
    val infoButton by style {
        width(18.px)
        height(18.px)
        minWidth(18.px)
        padding(0.px, 0.px)
        margin(0.px)
        gap(0.px)
    }
    val infoIcon by style {
        width(16.px)
        height(16.px)
        marginRight(0.px)
        property("filter", "brightness(0) invert(1)")
    }
    val box by style {
        width(100.percent)
        height(100.percent)
        padding(18.px)
        overflow("auto")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(10.px)
        boxSizing("border-box")
    }
    val title by style {
        margin(0.px)
        color(Color.white)
        fontSize(17.px)
        fontWeight("900")
        textAlign("center")
        property("white-space", "nowrap")
    }
    val table by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "1fr 1fr")
        property("grid-auto-rows", "minmax(38px, max-content)")
        alignContent(AlignContent.Start)
        gap(0.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.12))
        borderRadius(5.px)
        overflow("hidden")
    }
    val tableRow by style {
        display(DisplayStyle.Contents)
    }
    val sectionRow by style {
        display(DisplayStyle.Contents)
    }
    val sectionLabel by style {
        property("grid-column", "1 / -1")
        padding(7.px, 12.px)
        color(rgba(255, 255, 255, 0.88))
        fontSize(12.px)
        fontWeight("900")
        backgroundColor(rgba(255, 255, 255, 0.075))
        property("border-bottom", "1px solid rgba(255,255,255,0.10)")
        property("letter-spacing", "0")
    }
    val tableCell by style {
        minWidth(0.px)
        padding(9.px, 12.px)
        color(rgba(255, 255, 255, 0.62))
        fontSize(15.px)
        fontWeight("800")
        property("border-bottom", "1px solid rgba(255,255,255,0.08)")
        backgroundColor(rgba(255, 255, 255, 0.03))
        property("white-space", "nowrap")
        overflow("hidden")
        property("text-overflow", "ellipsis")
    }
    val tableValue by style {
        minWidth(0.px)
        padding(9.px, 12.px)
        color(Color.white)
        fontSize(15.px)
        fontWeight("900")
        textAlign("right")
        property("border-bottom", "1px solid rgba(255,255,255,0.08)")
        backgroundColor(rgba(255, 255, 255, 0.02))
        property("white-space", "nowrap")
        overflow("hidden")
        property("text-overflow", "ellipsis")
    }
}
